import { db } from './config';
import { doc, getDoc, updateDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { handleFirebaseAuthError } from '@/utils/firebaseErrorHandler';

// Support both old and new tier names for backward compatibility
export type SubscriptionPlan = 'communicate' | 'operate' | 'comply' | 'enterprise' | 'starter' | 'standard' | 'pro' | null;

export interface Subscription {
  plan: SubscriptionPlan;
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'incomplete' | 'incomplete_expired';
  stripeSubscriptionId?: string;
  stripePriceId?: string;
  stripeCustomerId?: string;
  quantity?: number; // Number of locations/licenses
  currentPeriodStart?: any;
  currentPeriodEnd?: any;
  cancelAtPeriodEnd?: boolean;
  updatedAt?: any;
  paymentFailedAt?: any; // Timestamp when payment failed (for grace period calculation)
}

/**
 * Normalize subscription plan from old tier names to new tier names
 * Backward compatibility: standard -> comply
 * Other old tiers (starter, pro) are not used, so we can map them as needed
 */
export function normalizeSubscriptionPlan(plan: SubscriptionPlan): 'communicate' | 'operate' | 'comply' | 'enterprise' | null {
  if (!plan) return null;
  
  // Map old tier names to new tier names
  switch (plan) {
    case 'standard':
      // Standard tier maps to comply tier (backward compatibility)
      return 'comply';
    case 'starter':
    case 'pro':
      // These old tiers are not used, but if found, treat as comply for safety
      return 'comply';
    case 'communicate':
    case 'operate':
    case 'comply':
    case 'enterprise':
      // New tier names pass through unchanged
      return plan;
    default:
      return null;
  }
}

export interface SubscriptionResult {
  success: boolean;
  data?: Subscription;
  error?: string;
}

export const subscriptionService = {
  // Get organization subscription (with fallback to user subscription for migration)
  getSubscription: async (organizationId: string, userId?: string): Promise<SubscriptionResult> => {
    try {
      const orgRef = doc(db, 'organizations', organizationId);
      const orgSnap = await getDoc(orgRef);

      if (!orgSnap.exists()) {
        return { success: false, error: 'Organization not found' };
      }

      const orgData = orgSnap.data();
      
      console.log('🔍 Organization data:', {
        organizationId,
        hasSubscription: !!orgData.subscription,
        subscriptionData: orgData.subscription,
        stripeCustomerId: orgData.stripeCustomerId,
      });
      
      // First, check if organization has subscription (new way)
      // Check if subscription object exists (even if plan is null)
      if (orgData.subscription) {
        const subscription: Subscription = orgData.subscription;
        console.log('✅ Found subscription in organization:', subscription);
        return { success: true, data: subscription };
      }
      
      console.log('⚠️ No subscription object found in organization document');

      // Fallback: Check user document for subscription (legacy/migration support)
      if (userId) {
        try {
          const userRef = doc(db, 'users', userId);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            const userData = userSnap.data();
            if (userData.subscription && userData.subscription.plan) {
              const subscription: Subscription = userData.subscription;
              
              // Optionally migrate subscription from user to organization
              // This happens automatically on read for seamless migration
              console.log('⚠️ Found legacy subscription in user document. Consider migrating to organization.');
              
              return { success: true, data: subscription };
            }
          }
        } catch (userError) {
          console.warn('Error checking user subscription (fallback):', userError);
        }
      }

      // No subscription found in either location
      return { 
        success: true, 
        data: {
          plan: null,
          status: 'incomplete',
          quantity: 0,
        }
      };
    } catch (error: any) {
      console.error('Error getting subscription:', error);
      // Handle auth errors (token expiration, etc.)
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to get subscription' };
    }
  },

  // Update subscription (typically called from webhooks)
  updateSubscription: async (
    organizationId: string,
    subscriptionData: Partial<Subscription>
  ): Promise<SubscriptionResult> => {
    try {
      const orgRef = doc(db, 'organizations', organizationId);
      await updateDoc(orgRef, {
        subscription: {
          ...subscriptionData,
          updatedAt: serverTimestamp(),
        },
      });

      return { success: true };
    } catch (error: any) {
      console.error('Error updating subscription:', error);
      // Handle auth errors (token expiration, etc.)
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message || 'Failed to update subscription' };
    }
  },

  // Get subscription with real-time listener for organization (with user fallback)
  getSubscriptionByOrganizationId: (
    organizationId: string,
    callback: (subscription: Subscription | null) => void,
    userId?: string
  ) => {
    const orgRef = doc(db, 'organizations', organizationId);
    
    const unsubscribe = onSnapshot(
      orgRef,
      async (orgSnap) => {
        if (orgSnap.exists()) {
          const orgData = orgSnap.data();
          
          console.log('🔍 Real-time listener - Organization data:', {
            organizationId,
            hasSubscription: !!orgData.subscription,
            subscriptionData: orgData.subscription,
          });
          
          // First check organization subscription
          // Check if subscription object exists (even if plan is null)
          if (orgData.subscription) {
            console.log('✅ Real-time listener - Found subscription:', orgData.subscription);
            console.log('📤 Real-time listener - Calling callback with subscription:', {
              plan: orgData.subscription.plan,
              status: orgData.subscription.status,
              quantity: orgData.subscription.quantity,
              fullObject: orgData.subscription,
            });
            callback(orgData.subscription);
            return;
          }
          
          console.log('⚠️ Real-time listener - No subscription object found');
          
          // Fallback: Check user document for legacy subscription
          if (userId) {
            try {
              const userRef = doc(db, 'users', userId);
              const userSnap = await getDoc(userRef);
              
              if (userSnap.exists()) {
                const userData = userSnap.data();
                if (userData.subscription && userData.subscription.plan) {
                  callback(userData.subscription);
                  return;
                }
              }
            } catch (userError) {
              console.warn('Error checking user subscription (fallback):', userError);
            }
          }
          
          // No subscription found
          callback({
            plan: null,
            status: 'incomplete',
            quantity: 0,
          });
        } else {
          callback(null);
        }
      },
      async (error) => {
        console.error('Error getting subscription:', error);
        // Handle auth errors (token expiration, etc.)
        await handleFirebaseAuthError(error);
        callback(null);
      }
    );

    return unsubscribe;
  },

  // Check if organization has active subscription (with grace period for past_due)
  hasActiveSubscription: async (organizationId: string): Promise<boolean> => {
    const result = await subscriptionService.getSubscription(organizationId);
    if (!result.success || !result.data) {
      return false;
    }

    const subscription = result.data;
    const status = subscription.status;

    // Allow active and trialing subscriptions
    if (status === 'active' || status === 'trialing') {
      return true;
    }

    // Allow past_due subscriptions within grace period (7 days)
    if (status === 'past_due') {
      const GRACE_PERIOD_DAYS = 7;
      const paymentFailedAt = subscription.paymentFailedAt;
      
      if (paymentFailedAt) {
        try {
          const failedDate = paymentFailedAt.toDate ? paymentFailedAt.toDate() : new Date(paymentFailedAt);
          const daysSinceFailure = (Date.now() - failedDate.getTime()) / (1000 * 60 * 60 * 24);
          return daysSinceFailure <= GRACE_PERIOD_DAYS;
        } catch (error) {
          console.error('Error calculating grace period:', error);
          // If we can't calculate, use updatedAt as fallback
          const updatedAt = subscription.updatedAt;
          if (updatedAt) {
            try {
              const updatedDate = updatedAt.toDate ? updatedAt.toDate() : new Date(updatedAt);
              const daysSinceUpdate = (Date.now() - updatedDate.getTime()) / (1000 * 60 * 60 * 24);
              return daysSinceUpdate <= GRACE_PERIOD_DAYS;
            } catch (e) {
              // If we can't parse dates, deny access to be safe
              return false;
            }
          }
        }
      } else {
        // If no paymentFailedAt, check updatedAt as fallback
        const updatedAt = subscription.updatedAt;
        if (updatedAt) {
          try {
            const updatedDate = updatedAt.toDate ? updatedAt.toDate() : new Date(updatedAt);
            const daysSinceUpdate = (Date.now() - updatedDate.getTime()) / (1000 * 60 * 60 * 24);
            return daysSinceUpdate <= GRACE_PERIOD_DAYS;
          } catch (e) {
            return false;
          }
        }
      }
      // If no timestamps available, deny access
      return false;
    }

    // All other statuses (canceled, incomplete, incomplete_expired) are not active
    return false;
  },

  // Check if organization has plan feature
  hasFeature: async (
    organizationId: string,
    feature: 'ai_assistance' | 'asset_management' | 'qr_codes' | 'api' | 'sso' | 'forms' | 'checklists' | 'messaging' | 'assets' | 'create_custom_tasks' | 'compliance_auto_tasks' | 'compliance_notifications' | 'contracts_management' | 'client_side_encryption' | 'cmek' | 'vpc_service_controls' | 'immutable_audit_logging'
  ): Promise<boolean> => {
    const result = await subscriptionService.getSubscription(organizationId);
    if (!result.success || !result.data) {
      return false;
    }

    const rawPlan = result.data.plan;
    const status = result.data.status;

    // Normalize plan for backward compatibility (standard -> comply)
    const plan = normalizeSubscriptionPlan(rawPlan);

    // Must have active subscription (check using hasActiveSubscription to include grace period)
    const isActive = await subscriptionService.hasActiveSubscription(organizationId);
    if (!isActive) {
      return false;
    }

    // Also explicitly deny incomplete_expired status
    if (status === 'incomplete_expired') {
      return false;
    }

    // Feature gating by plan
    // Tier 1: Communicate (free) - only team communication
    // Tier 2: Operate - can create tasks from checklists/forms/messages, but no compliance auto-tasks/notifications, no contracts
    // Tier 3: Comply - full access including compliance features and contracts
    
    switch (feature) {
      // Messaging is available in all tiers (Communicate, Operate, Comply)
      case 'messaging':
        return plan === 'communicate' || plan === 'operate' || plan === 'comply';
      
      // Legacy features - maintain backward compatibility
      case 'ai_assistance':
      case 'asset_management':
      case 'qr_codes':
      case 'forms':
      case 'checklists':
      case 'assets':
        // These features are available in operate and comply tiers
        return plan === 'operate' || plan === 'comply';

      case 'api':
      case 'sso':
        // These features are only in comply tier
        return plan === 'comply';

      // New tier-specific features
      case 'create_custom_tasks':
        // Can create tasks from checklists, forms, messages in operate and comply tiers
        return plan === 'operate' || plan === 'comply';

      case 'compliance_auto_tasks':
        // Compliance tasks are automatically generated only in comply tier
        return plan === 'comply';

      case 'compliance_notifications':
        // Compliance notifications are sent only in comply tier
        return plan === 'comply';

      case 'contracts_management':
        // Contracts management is only available in comply tier
        return plan === 'comply';

      // Enterprise Plan Only Features (Bank-Grade Security)
      case 'client_side_encryption':
      case 'cmek':
      case 'vpc_service_controls':
      case 'immutable_audit_logging':
        // These features are ONLY available in enterprise tier
        return plan === 'enterprise';

      default:
        return false;
    }
  },

  // Check if organization can add more locations
  canAddLocation: async (organizationId: string, currentLocationCount: number): Promise<{ canAdd: boolean; maxLocations?: number; error?: string }> => {
    const result = await subscriptionService.getSubscription(organizationId);
    if (!result.success || !result.data) {
      return { canAdd: false, error: 'Subscription not found' };
    }

    const subscription = result.data;
    const status = subscription.status;
    const plan = normalizeSubscriptionPlan(subscription.plan);

    // Communicate (free) plan: allow adding multiple locations - no paid subscription required
    if (plan === 'communicate' || !subscription.plan) {
      const COMMUNICATE_PLAN_LOCATION_LIMIT = 10;
      if (currentLocationCount >= COMMUNICATE_PLAN_LOCATION_LIMIT) {
        return {
          canAdd: false,
          maxLocations: COMMUNICATE_PLAN_LOCATION_LIMIT,
          error: 'Location limit reached',
        };
      }
      return { canAdd: true, maxLocations: COMMUNICATE_PLAN_LOCATION_LIMIT };
    }

    // Paid plans: must have active subscription
    const isActive = await subscriptionService.hasActiveSubscription(organizationId);
    if (!isActive) {
      // Provide specific error message based on status
      if (status === 'past_due') {
        return { canAdd: false, error: 'Payment is past due. Please update your payment method to continue.' };
      } else if (status === 'incomplete_expired') {
        return { canAdd: false, error: 'Subscription setup expired. Please create a new subscription.' };
      } else if (status === 'canceled') {
        return { canAdd: false, error: 'Subscription is canceled. Please renew your subscription.' };
      }
      return { canAdd: false, error: 'No active subscription' };
    }

    // Get current quantity (licensed locations)
    const licensedLocations = subscription.quantity || 0;

    // Check if current location count is below licensed amount
    if (currentLocationCount >= licensedLocations) {
      return { canAdd: false, maxLocations: licensedLocations, error: 'Location limit reached' };
    }

    return { canAdd: true, maxLocations: licensedLocations };
  },
};

